import React, { useState, useEffect } from "react";
import { X } from "lucide-react";
import { useDriver } from "./DriverContext";
import { Input } from "./Input";
import { DriverVehicleInfo } from "./types";
import { ImageUploader } from "./ImageUploader";
import { useAuth } from "../../../../context/AuthContext";
import { ClinicDriverService } from "../../../../services/api/clinic.driver.service";
import toast from "react-hot-toast";

export const VehicleInfoForm: React.FC = () => {
  const { state, dispatch } = useDriver();
  const { personalInfo, vehicleInfo } = state;
  const { user } = useAuth();
  const clinicId = user?.role === "clinic" ? user?.id : "";
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Validate all fields whenever vehicleInfo changes
  useEffect(() => {
    const fieldsToValidate: Array<keyof DriverVehicleInfo> = [
      "vehicleBrand",
      "vehicleModel",
      "vehicleNumber",
      "driverLicenseNumber",
      "vehicleDlFrontPicture",
      "vehicleDlBackPicture",
      "vehicleRcFrontPicture",
      "vehicleRcBackPicture",
      "vehicleInsurancePicture",
    ];

    fieldsToValidate.forEach((field) => {
      validateField(field, vehicleInfo[field] as string | File);
    });
  }, [vehicleInfo]);

  const updateVehicleInfo = (field: keyof DriverVehicleInfo, value: string) => {
    dispatch({
      type: "UPDATE_VEHICLE_INFO",
      payload: { [field]: value },
    });

    // Mark field as touched when user starts typing
    if (!touched[field]) {
      setTouched((prev) => ({ ...prev, [field]: true }));
    }
  };

  const handleBlur = (field: keyof DriverVehicleInfo) => {
    if (!touched[field]) {
      setTouched((prev) => ({ ...prev, [field]: true }));
    }
  };

  const validateField = (
    field: keyof DriverVehicleInfo,
    value: string | File | null | undefined
  ): boolean => {
    let error = "";

    switch (field) {
      case "vehicleBrand":
        if (!value) error = "Vehicle brand is required";
        break;
      case "vehicleModel":
        if (!value) error = "Vehicle model is required";
        break;
      case "vehicleNumber":
        if (!value) {
          error = "Vehicle number is required";
        } else if (
          !/^[A-Z]{2}[0-9]{2}[A-Z]{1,2}[0-9]{4}$/.test(value as string)
        ) {
          error = "Invalid format (e.g., MH02AB1234)";
        }
        break;
      case "driverLicenseNumber":
        if (!value) {
          error = "DL number is required";
        } else if (!/^[A-Z]{2}[0-9]{13}$/.test(value as string)) {
          error = "Invalid format (e.g., MH0220210000001)";
        }
        break;
      case "vehicleDlFrontPicture":
        if (!value) error = "DL front picture is required";
        break;
      case "vehicleDlBackPicture":
        if (!value) error = "DL back picture is required";
        break;
      case "vehicleRcFrontPicture":
        if (!value) error = "RC front picture is required";
        break;
      case "vehicleRcBackPicture":
        if (!value) error = "RC back picture is required";
        break;
      case "vehicleInsurancePicture":
        if (!value) error = "Insurance picture is required";
        break;
      default:
        break;
    }

    setErrors((prev) => ({ ...prev, [field]: error }));
    return !error;
  };

  const handleFileUpload = (field: keyof DriverVehicleInfo, file: File) => {
    dispatch({
      type: "UPDATE_VEHICLE_INFO",
      payload: { [field]: file },
    });
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, file);
  };

  const handleImageDelete = (field: keyof DriverVehicleInfo) => {
    dispatch({
      type: "UPDATE_VEHICLE_INFO",
      payload: { [field]: null },
    });
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, null);
  };

  const validateForm = (): boolean => {
    const requiredFields: Array<keyof DriverVehicleInfo> = [
      "vehicleBrand",
      "vehicleModel",
      "vehicleNumber",
      "driverLicenseNumber",
      "vehicleDlFrontPicture",
      "vehicleDlBackPicture",
      "vehicleRcFrontPicture",
      "vehicleRcBackPicture",
      "vehicleInsurancePicture",
    ];

    // Check if all required fields have values and no errors
    return requiredFields.every((field) => {
      const value = vehicleInfo[field];
      return (
        value !== null && value !== undefined && value !== "" && !errors[field]
      );
    });
  };

  const handleBack = () => {
    dispatch({ type: "SET_STEP", payload: "personal" });
  };

  const handleSubmit = async () => {
    // Mark all fields as touched when trying to submit
    const allFields: Array<keyof DriverVehicleInfo> = [
      "vehicleBrand",
      "vehicleModel",
      "vehicleNumber",
      "driverLicenseNumber",
      "vehicleDlFrontPicture",
      "vehicleDlBackPicture",
      "vehicleRcFrontPicture",
      "vehicleRcBackPicture",
      "vehicleInsurancePicture",
    ];

    const newTouched: Record<string, boolean> = {};
    allFields.forEach((field) => {
      newTouched[field] = true;
    });
    setTouched(newTouched);

    // Validate all fields
    allFields.forEach((field) => {
      validateField(field, vehicleInfo[field] as string | File);
    });

    if (!validateForm()) {
      toast.error("Please fill all required fields correctly");
      return;
    }

    try {
      // Prepare the data payload
      const data = {
        clinicId: clinicId,
        type: personalInfo.driverType,
        education: personalInfo.education,
        dateOfBirth: personalInfo.dateOfBirth,
        address: personalInfo.address,
        vehicleBrand: vehicleInfo.vehicleBrand,
        vehicleNumber: vehicleInfo.vehicleNumber,
        drivingLicence: vehicleInfo.driverLicenseNumber,
        blocked: false,
        aadharNumber: personalInfo.aadharNumber,
        phoneNumber: personalInfo.phoneNumber,
        username: personalInfo.username,
        password: personalInfo.password,
        email: personalInfo.email,
        name: personalInfo.name,
      };

      // Prepare the files payload
      const files = {
        profileImage: personalInfo.profilePicture,
        aadharFrontImage: personalInfo.aadharFrontPicture,
        aadharBackImage: personalInfo.aadharBackPicture,
        dlFrontImage: vehicleInfo.vehicleDlFrontPicture,
        dlBackImage: vehicleInfo.vehicleDlBackPicture,
        vehicleRcFront: vehicleInfo.vehicleRcFrontPicture,
        vehicleRcBack: vehicleInfo.vehicleRcBackPicture,
        vehicleInsurance: vehicleInfo.vehicleInsurancePicture,
      };

      // Call the API
      await ClinicDriverService.createDriver(data, files);

      toast.success("Driver created successfully!");
      dispatch({ type: "RESET" });
      dispatch({ type: "CLOSE_FORM" });
    } catch (error) {
      console.error("Error creating driver:", error);

      // Extract error message from the API response
      let errorMessage = "Failed to create driver";

      if (error?.response?.data?.message) {
        // If the API returns a message field
        errorMessage = error.response.data.message;
      } else if (error?.response?.data?.error) {
        // If the API returns an error field
        errorMessage = error.response.data.error;
      } else if (error?.response?.data) {
        // If the API returns error data directly
        if (typeof error.response.data === "string") {
          errorMessage = error.response.data;
        } else if (error.response.data.errors) {
          // Handle validation errors array
          errorMessage = error.response.data.errors[0] || errorMessage;
        }
      } else if (error?.message) {
        // Fallback to error message
        errorMessage = error.message;
      }

      toast.error(errorMessage);
    }
  };

  return (
    <div className="w-[600px] mx-auto bg-white rounded-lg shadow-lg p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-lg font-medium text-green-600">
          ADD DRIVER VEHICLE INFORMATION
        </h2>
        <X
          className="w-5 h-5 text-gray-400 cursor-pointer"
          onClick={() => dispatch({ type: "CLOSE_FORM" })}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">
            Vehicle DL Front Picture <span className="text-red-500">*</span>
          </div>
          <ImageUploader
            label=""
            image={vehicleInfo.vehicleDlFrontPicture ?? null}
            inputId="vehicle-dl-front-back-image"
            onUpload={(file) => handleFileUpload("vehicleDlFrontPicture", file)}
            onDelete={() => handleImageDelete("vehicleDlFrontPicture")}
            hasError={
              !!errors.vehicleDlFrontPicture && touched.vehicleDlFrontPicture
            }
          />
          {errors.vehicleDlFrontPicture && touched.vehicleDlFrontPicture && (
            <p className="text-red-500 text-xs mt-1">
              {errors.vehicleDlFrontPicture}
            </p>
          )}
        </div>

        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">
            Vehicle DL Back Picture <span className="text-red-500">*</span>
          </div>
          <ImageUploader
            label=""
            image={vehicleInfo.vehicleDlBackPicture ?? null}
            inputId="vehicle-dl-back-image"
            onUpload={(file) => handleFileUpload("vehicleDlBackPicture", file)}
            onDelete={() => handleImageDelete("vehicleDlBackPicture")}
            hasError={
              !!errors.vehicleDlBackPicture && touched.vehicleDlBackPicture
            }
          />
          {errors.vehicleDlBackPicture && touched.vehicleDlBackPicture && (
            <p className="text-red-500 text-xs mt-1">
              {errors.vehicleDlBackPicture}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">
            Vehicle RC Front Picture <span className="text-red-500">*</span>
          </div>
          <ImageUploader
            label=""
            image={vehicleInfo.vehicleRcFrontPicture ?? null}
            inputId="vehicle-rc-front-image"
            onUpload={(file) => handleFileUpload("vehicleRcFrontPicture", file)}
            onDelete={() => handleImageDelete("vehicleRcFrontPicture")}
            hasError={
              !!errors.vehicleRcFrontPicture && touched.vehicleRcFrontPicture
            }
          />
          {errors.vehicleRcFrontPicture && touched.vehicleRcFrontPicture && (
            <p className="text-red-500 text-xs mt-1">
              {errors.vehicleRcFrontPicture}
            </p>
          )}
        </div>

        <div>
          <div className="text-sm font-medium text-gray-700 mb-1">
            Vehicle RC Back Picture <span className="text-red-500">*</span>
          </div>
          <ImageUploader
            label=""
            image={vehicleInfo.vehicleRcBackPicture ?? null}
            inputId="vehicle-rc-back-image"
            onUpload={(file) => handleFileUpload("vehicleRcBackPicture", file)}
            onDelete={() => handleImageDelete("vehicleRcBackPicture")}
            hasError={
              !!errors.vehicleRcBackPicture && touched.vehicleRcBackPicture
            }
          />
          {errors.vehicleRcBackPicture && touched.vehicleRcBackPicture && (
            <p className="text-red-500 text-xs mt-1">
              {errors.vehicleRcBackPicture}
            </p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Input
            label="Vehicle Brand"
            value={vehicleInfo.vehicleBrand}
            onChange={(value) => updateVehicleInfo("vehicleBrand", value)}
            onBlur={() => handleBlur("vehicleBrand")}
            placeholder="Enter Vehicle Brand"
            required
            hasError={!!errors.vehicleBrand && touched.vehicleBrand}
          />
          {errors.vehicleBrand && touched.vehicleBrand && (
            <p className="text-red-500 text-xs mt-1">{errors.vehicleBrand}</p>
          )}
        </div>
        <div>
          <Input
            label="Vehicle Model"
            value={vehicleInfo.vehicleModel}
            onChange={(value) => updateVehicleInfo("vehicleModel", value)}
            onBlur={() => handleBlur("vehicleModel")}
            placeholder="Enter Vehicle Model"
            required
            hasError={!!errors.vehicleModel && touched.vehicleModel}
          />
          {errors.vehicleModel && touched.vehicleModel && (
            <p className="text-red-500 text-xs mt-1">{errors.vehicleModel}</p>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Input
            label="Vehicle Number"
            value={vehicleInfo.vehicleNumber}
            onChange={(value) => updateVehicleInfo("vehicleNumber", value)}
            onBlur={() => handleBlur("vehicleNumber")}
            placeholder="Enter Vehicle Number"
            required
            hasError={!!errors.vehicleNumber && touched.vehicleNumber}
          />
          {errors.vehicleNumber && touched.vehicleNumber && (
            <p className="text-red-500 text-xs mt-1">{errors.vehicleNumber}</p>
          )}
        </div>
        <div>
          <Input
            label="Driver Licence number"
            value={vehicleInfo.driverLicenseNumber}
            onChange={(value) =>
              updateVehicleInfo("driverLicenseNumber", value)
            }
            onBlur={() => handleBlur("driverLicenseNumber")}
            placeholder="Enter DL Number"
            required
            hasError={
              !!errors.driverLicenseNumber && touched.driverLicenseNumber
            }
          />
          {errors.driverLicenseNumber && touched.driverLicenseNumber && (
            <p className="text-red-500 text-xs mt-1">
              {errors.driverLicenseNumber}
            </p>
          )}
        </div>
      </div>

      <div>
        <div className="text-sm font-medium text-gray-700 mb-1">
          Vehicle Insurance Picture <span className="text-red-500">*</span>
        </div>
        <ImageUploader
          label=""
          image={vehicleInfo.vehicleInsurancePicture ?? null}
          inputId="vehicle-insurance-image"
          onUpload={(file) => handleFileUpload("vehicleInsurancePicture", file)}
          onDelete={() => handleImageDelete("vehicleInsurancePicture")}
          hasError={
            !!errors.vehicleInsurancePicture && touched.vehicleInsurancePicture
          }
        />
        {errors.vehicleInsurancePicture && touched.vehicleInsurancePicture && (
          <p className="text-red-500 text-xs mt-1">
            {errors.vehicleInsurancePicture}
          </p>
        )}
      </div>

      <div className="flex justify-between gap-3 mt-6">
        <button
          onClick={handleBack}
          className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
        >
          Back
        </button>
        <div className="flex gap-3">
          <button
            className="px-4 py-2 text-gray-600 bg-gray-200 rounded-lg hover:bg-gray-300 transition-colors"
            onClick={() => dispatch({ type: "CLOSE_FORM" })}
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};
